import type { SafetyControlReviewPayload } from '../../services/reportsApi';
import { buildSafetyControlReviewForAlias } from '../policies/policyPacks';

export type CorrectiveActionControlReviewDraft = {
  controlObjective: string;
  controlLevel: string;
  potentialControlGap: string;
  fieldVerificationQuestion: string;
  verificationStatus: string;
  closureRequirement: string;
  followUpObservation: string;
  effectivenessBoundary: string;
};

function asText(value: unknown): string {
  return String(value || '').trim();
}

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function readPersistedControlReview(
  correctiveAction: Record<string, unknown> | null | undefined,
): SafetyControlReviewPayload | null {
  const review = asObject(correctiveAction?.safety_control_review);
  return Object.keys(review).length > 0 ? (review as SafetyControlReviewPayload) : null;
}

export function buildControlReviewDraft(args: {
  alias?: string | null;
  persisted?: SafetyControlReviewPayload | null;
  workflowReview?: SafetyControlReviewPayload | null;
}): CorrectiveActionControlReviewDraft {
  const registry = args.alias ? buildSafetyControlReviewForAlias(args.alias) : null;
  const persisted = args.workflowReview || args.persisted || null;
  const questions = Array.isArray(registry?.field_verification_questions)
    ? registry?.field_verification_questions
    : [];
  const gaps = Array.isArray(registry?.potential_control_gaps)
    ? registry?.potential_control_gaps
    : [];
  const closure = Array.isArray(registry?.closure_requirements)
    ? registry?.closure_requirements
    : [];
  return {
    controlObjective: asText(persisted?.control_objective) || asText(registry?.control_objective),
    controlLevel: asText(persisted?.control_level),
    potentialControlGap: asText(persisted?.potential_control_gap) || asText(gaps?.[0]),
    fieldVerificationQuestion:
      asText(persisted?.field_verification_question) || asText(questions?.[0]),
    verificationStatus:
      asText(persisted?.verification_status)
      || asText(registry?.verification_status)
      || 'pending_field_verification',
    closureRequirement: asText(persisted?.closure_requirement) || asText(closure?.[0]),
    followUpObservation: asText(persisted?.follow_up_observation),
    effectivenessBoundary:
      asText(persisted?.effectiveness_boundary) || asText(registry?.effectiveness_boundary),
  };
}

export function buildControlReviewPatch(
  draft: CorrectiveActionControlReviewDraft,
): SafetyControlReviewPayload {
  return {
    control_objective: asText(draft.controlObjective) || null,
    control_level: asText(draft.controlLevel) || null,
    potential_control_gap: asText(draft.potentialControlGap) || null,
    field_verification_question: asText(draft.fieldVerificationQuestion) || null,
    verification_status: asText(draft.verificationStatus) || 'pending_field_verification',
    closure_requirement: asText(draft.closureRequirement) || null,
    effectiveness_boundary: asText(draft.effectivenessBoundary) || null,
    follow_up_observation: asText(draft.followUpObservation) || null,
  };
}

export function controlReviewDraftDirty(
  left: CorrectiveActionControlReviewDraft,
  right: CorrectiveActionControlReviewDraft,
): boolean {
  return (
    left.controlLevel !== right.controlLevel
    || left.potentialControlGap !== right.potentialControlGap
    || left.fieldVerificationQuestion !== right.fieldVerificationQuestion
    || left.closureRequirement !== right.closureRequirement
    || left.followUpObservation !== right.followUpObservation
  );
}
